import { notifyError } from "@/lib/notify-error";
import { useEffect, useId, useState } from "react";
import { useMutation } from "convex/react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowUp,
  Check,
  ChevronDown,
  Edit,
  Loader2,
  MessageSquare,
  Save,
  Trash2,
} from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod/v4";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { CountBadge } from "@/components/semantic-badges";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useCanEdit, useOnEditAccessLost } from "@/lib/capabilities";

export const REV_PILOT_NOTE_LABELS = [
  "General",
  "Carpenter",
  "Electrician",
  "Plumber",
  "HVAC / Mechanical",
  "Sheet Metal",
  "Controls / BAS",
  "Fire Protection (Sprinkler)",
  "Millwork",
  "Architectural Metal & Glass",
  "Audio/Visual",
  "Tele/Data",
  "Carpet/Flooring",
  "Tile/Stone",
  "Demolition",
  "Doors/Frames",
  "Other",
] as const;

export interface RevPilotNote {
  _id: Id<"revPilotChangeGroupNotes">;
  changeGroupId: Id<"revPilotChangeGroups">;
  body: string;
  label: string;
}

const NoteSchema = z.object({
  body: z.string().min(1, "Description is required"),
  label: z.string().min(1, "Trade/label is required"),
});

type NoteFormValues = z.infer<typeof NoteSchema>;

interface CompactTradeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  id?: string;
}

export function CompactTradeSelector({
  value,
  onChange,
  disabled,
  id,
}: CompactTradeSelectorProps) {
  const reactId = useId();
  const customInputId = `${id ?? reactId}-custom-label`;
  const [open, setOpen] = useState(false);
  const [customValue, setCustomValue] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const isCustom = value.length > 0 && !REV_PILOT_NOTE_LABELS.includes(value as (typeof REV_PILOT_NOTE_LABELS)[number]);

  const handleSelect = (label: string) => {
    if (label === "__custom") {
      setShowCustomInput(true);
      return;
    }
    onChange(label);
    setOpen(false);
    setShowCustomInput(false);
  };

  const handleCustomSubmit = () => {
    if (customValue.trim()) {
      onChange(customValue.trim());
      setOpen(false);
      setShowCustomInput(false);
      setCustomValue("");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          size="compact"
          className="w-full justify-between border-dashed normal-case tracking-normal"
        >
          <span className={isCustom ? "" : "text-muted-foreground"}>
            {value}
          </span>
          <ChevronDown className="size-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        {showCustomInput ? (
          <div className="p-2 space-y-2">
            <Label htmlFor={customInputId} className="text-xs">
              Custom label
            </Label>
            <Input
              id={customInputId}
              placeholder="Enter custom label..."
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              className="h-8 text-xs"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCustomSubmit();
                if (e.key === "Escape") {
                  setShowCustomInput(false);
                  setCustomValue("");
                }
              }}
              autoFocus
            />
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="xs"
                className="flex-1"
                onClick={() => {
                  setShowCustomInput(false);
                  setCustomValue("");
                }}
              >
                Cancel
              </Button>
              <Button
                size="xs"
                className="flex-1"
                onClick={handleCustomSubmit}
                disabled={!customValue.trim()}
              >
                Add
              </Button>
            </div>
          </div>
        ) : (
          <Command>
            <CommandInput placeholder="Search trades..." className="h-8 text-xs" />
            <CommandList className="max-h-48">
              <CommandEmpty className="py-3 text-xs">No matching trade.</CommandEmpty>
              <CommandGroup>
                {REV_PILOT_NOTE_LABELS.map((label) => (
                  <CommandItem
                    key={label}
                    value={label}
                    onSelect={() => handleSelect(label)}
                    className="text-xs"
                  >
                    <Check
                      aria-hidden="true"
                      className={cn("size-3.5", value === label ? "opacity-100" : "opacity-0")}
                    />
                    {label}
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                {/* Always offered, even when the search matches no preset trade. */}
                <CommandItem
                  forceMount
                  value="__custom"
                  onSelect={() => handleSelect("__custom")}
                  className="text-xs italic text-muted-foreground"
                >
                  + Custom label...
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface ChangeGroupNoteItemProps {
  note: RevPilotNote;
  onEdit: (note: RevPilotNote) => void;
  onDelete: (noteId: Id<"revPilotChangeGroupNotes">) => void;
  isSaving?: boolean;
}

export function ChangeGroupNoteItem({ note, onEdit, onDelete, isSaving }: ChangeGroupNoteItemProps) {
  const canEdit = useCanEdit();
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {note.label}
        </span>
        {canEdit ? (
          <div className="flex gap-0.5">
            <IconButton
              label="Edit note"
              icon={Edit}
              size="xs"
              onClick={() => onEdit(note)}
              disabled={isSaving}
            />
            <IconButton
              label="Delete note"
              icon={Trash2}
              size="xs"
              className="hover:text-destructive"
              onClick={() => onDelete(note._id)}
              disabled={isSaving}
            />
          </div>
        ) : null}
      </div>
      <p className="whitespace-pre-wrap text-xs leading-relaxed">{note.body}</p>
    </div>
  );
}

interface ChangeGroupNoteEditFormProps {
  note: RevPilotNote;
  onSave: (noteId: Id<"revPilotChangeGroupNotes">, body: string, label: string) => void;
  onCancel: () => void;
  isSaving?: boolean;
}

export function ChangeGroupNoteEditForm({ note, onSave, onCancel, isSaving }: ChangeGroupNoteEditFormProps) {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<NoteFormValues>({
    resolver: zodResolver(NoteSchema as never),
    defaultValues: {
      body: note.body,
      label: note.label,
    },
  });
  const disabled = isSaving || isSubmitting;

  const handleSave = handleSubmit((data) => {
    onSave(note._id, data.body, data.label);
  });

  return (
    <form className="space-y-2" onSubmit={handleSave}>
      <div className="space-y-1.5">
        <Label htmlFor={`change-group-note-edit-trade-${note._id}`} className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Trade/Label
        </Label>
        <Controller
          name="label"
          control={control}
          render={({ field }) => (
            <CompactTradeSelector
              id={`change-group-note-edit-trade-${note._id}`}
              value={field.value}
              onChange={field.onChange}
              disabled={disabled}
            />
          )}
        />
      </div>
      {errors.label ? (
        <p className="text-[11px] text-destructive">{errors.label.message}</p>
      ) : null}
      <div className="space-y-1.5">
        <Label
          htmlFor={`change-group-note-edit-body-${note._id}`}
          className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
        >
          Description
        </Label>
        <Textarea
          id={`change-group-note-edit-body-${note._id}`}
          className="min-h-20 resize-none px-2.5 text-xs"
          {...register("body")}
          disabled={disabled}
        />
      </div>
      {errors.body ? (
        <p className="text-[11px] text-destructive">{errors.body.message}</p>
      ) : null}
      <div className="flex justify-end gap-1.5">
        <Button type="button" variant="outline" size="xs" onClick={onCancel} disabled={disabled}>
          Cancel
        </Button>
        <Button type="submit" size="xs" disabled={disabled}>
          <Save />
          Save
        </Button>
      </div>
    </form>
  );
}

interface ChangeGroupNotesPanelProps {
  changeGroupId: Id<"revPilotChangeGroups">;
  included: boolean;
  notes: RevPilotNote[] | undefined;
  showHeader?: boolean;
  initialEditingNoteId?: Id<"revPilotChangeGroupNotes"> | null;
}

export function ChangeGroupNotesPanel({
  changeGroupId,
  included,
  notes,
  showHeader = false,
  initialEditingNoteId = null,
}: ChangeGroupNotesPanelProps) {
  const createNote = useMutation(api.changeGroups.createNote);
  const updateNote = useMutation(api.changeGroups.updateNote);
  const deleteNote = useMutation(api.changeGroups.deleteNote);
  const [newBody, setNewBody] = useState("");
  const [newLabel, setNewLabel] = useState("General");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // Notes are project writes; reviewers read them with no composer or edit and delete controls.
  const canEdit = useCanEdit();
  useOnEditAccessLost(() => setEditingNoteId(null));

  useEffect(() => {
    setEditingNoteId(canEdit && initialEditingNoteId ? String(initialEditingNoteId) : null);
  }, [canEdit, initialEditingNoteId]);

  const handleCreateNote = async () => {
    setIsSaving(true);
    try {
      await createNote({ changeGroupId, body: newBody, label: newLabel });
      setNewBody("");
      setNewLabel("General");
      toast.success("Note added");
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to add note");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartEdit = (note: RevPilotNote) => {
    setEditingNoteId(String(note._id));
  };

  const handleUpdateNote = async (noteId: Id<"revPilotChangeGroupNotes">, body: string, label: string) => {
    setIsSaving(true);
    try {
      await updateNote({ noteId, body, label });
      setEditingNoteId(null);
      toast.success("Note updated");
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to update note");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteNote = async (noteId: Id<"revPilotChangeGroupNotes">) => {
    setIsSaving(true);
    try {
      await deleteNote({ noteId });
      toast.success("Note deleted");
    } catch (error) {
      notifyError(error, "Failed to delete note");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-col h-full">
      {showHeader && (
        <div className="flex items-center justify-between border-b px-4 py-3 bg-card">
          <div className="flex items-center gap-2">
            <MessageSquare className="size-4 text-muted-foreground" />
            <h4 className="text-sm font-medium">Narrative Notes</h4>
          </div>
          <CountBadge value={notes?.length ?? 0} label="notes" />
        </div>
      )}

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
        {notes === undefined ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            Loading notes...
          </div>
        ) : notes.length === 0 ? (
          <p className="text-xs text-muted-foreground py-1">No notes yet.</p>
        ) : (
          notes.map((note) => {
            const isEditing = editingNoteId === String(note._id);
            return (
              <div key={note._id} className="rounded-md border bg-background/80 p-2">
                {isEditing ? (
                  <ChangeGroupNoteEditForm
                    note={note}
                    onSave={handleUpdateNote}
                    onCancel={() => setEditingNoteId(null)}
                    isSaving={isSaving}
                  />
                ) : (
                  <ChangeGroupNoteItem
                    note={note}
                    onEdit={handleStartEdit}
                    onDelete={handleDeleteNote}
                    isSaving={isSaving}
                  />
                )}
              </div>
            );
          })
        )}
      </div>

      {canEdit ? (
        <div className="space-y-2 border-t p-3 bg-card">
          {!included ? (
            <div className="rounded-md bg-muted p-2">
              <p className="text-xs font-medium">Excluded change</p>
              <p className="text-xs text-muted-foreground">
                Adding notes is disabled while this change group is excluded.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label
                  htmlFor={`change-group-note-trade-${changeGroupId}`}
                  className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide"
                >
                  Trade/Label
                </Label>
                <CompactTradeSelector
                  id={`change-group-note-trade-${changeGroupId}`}
                  value={newLabel}
                  onChange={setNewLabel}
                  disabled={isSaving}
                />
              </div>
              <div className="space-y-1.5">
                <Label
                  htmlFor={`change-group-note-description-${changeGroupId}`}
                  className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
                >
                  Description
                </Label>
                <div className="relative">
                  <Textarea
                    id={`change-group-note-description-${changeGroupId}`}
                    className="min-h-28 resize-none px-2.5 pe-9 pb-10 text-xs"
                    placeholder="Describe this change..."
                    value={newBody}
                    onChange={(event) => setNewBody(event.target.value)}
                    disabled={isSaving}
                  />
                  <IconButton
                    label="Add note"
                    icon={ArrowUp}
                    size="xs"
                    variant="default"
                    className="absolute bottom-3 right-2"
                    onClick={() => void handleCreateNote()}
                    disabled={isSaving || !newBody.trim() || !newLabel.trim()}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
